import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCcw, Trash2, Users, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  listTipPools,
  createTipPool,
  updateTipPool,
  deleteTipPool,
  listTipPoolAssignments,
  createTipPoolAssignment,
  updateTipPoolAssignment,
  deleteTipPoolAssignment,
  getTipDistributionSummary,
  rerunTipDistribution,
  fetchPayrollPeriods,
  type TipPool,
  type TipPoolAssignment,
  type TipPoolMethod,
  type TipDistributionSummary,
  type PayrollPeriodSummary,
  type CreateTipPoolPayload,
  type UpdateTipPoolPayload,
  type UpsertTipPoolAssignmentPayload,
} from '@/lib/api/timekeeping';
import { listPayrollEmployees, type PayrollEmployeeRecord } from '@/lib/api/payrollBridge';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { summarizePoolTotals } from './TipPoolingWorkspace.helpers';

const DEFAULT_CONFIGS: Record<TipPoolMethod, Record<string, unknown>> = {
  hours: {
    includeOvertimeSeconds: true,
    includePremiumSeconds: false,
    overtimeMultiplier: 1,
    minimumSeconds: null,
    maximumSeconds: null,
    roundingMode: 'nearest',
    assignmentWeightMultiplier: true,
  },
  role_weight: {
    minimumSeconds: null,
    maximumSeconds: null,
    defaultWeight: 1,
    roleOverrides: {},
    roundingMode: 'nearest',
  },
  percentage: {
    splits: [],
    totalWeight: 100,
    precision: 2,
    remainderStrategy: 'carry',
    carryMetadataKey: 'remainderCents',
  },
};

const formatCurrency = (valueCents: number): string =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(valueCents / 100);

const safeJsonStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
};

const parseConfig = (value: string, fallback: Record<string, unknown>): Record<string, unknown> => {
  if (!value || value.trim().length === 0) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // fall through to fallback
  }

  return fallback;
};

const buildConfigForMethod = (method: TipPoolMethod, configJson: string): Record<string, unknown> => {
  const fallback = DEFAULT_CONFIGS[method] ?? {};
  return parseConfig(configJson, fallback);
};

const TipPoolFormSchema = z.object({
  name: z.string().min(1),
  method: z.enum(['hours', 'role_weight', 'percentage']),
  locationId: z.string().optional(),
  configJson: z.string().optional(),
  isActive: z.boolean(),
});

type TipPoolFormValues = {
  name: string;
  method: TipPoolMethod;
  locationId: string;
  configJson: string;
  isActive: boolean;
};

const AssignmentFormSchema = z.object({
  employeeId: z.string().min(1),
  weight: z.string().optional(),
  role: z.string().optional(),
  metadataJson: z.string().optional(),
});

type AssignmentFormValues = {
  employeeId: string;
  weight: string;
  role: string;
  metadataJson: string;
};

const preparePoolFormValues = (pool?: TipPool): TipPoolFormValues => ({
  name: pool?.name ?? '',
  method: (pool?.method ?? 'hours') as TipPoolMethod,
  locationId: pool?.locationId ?? '',
  configJson: safeJsonStringify(pool?.config ?? DEFAULT_CONFIGS[(pool?.method ?? 'hours') as TipPoolMethod]),
  isActive: pool?.isActive ?? true,
});

const defaultAssignmentFormValues = (): AssignmentFormValues => ({
  employeeId: '',
  weight: '1',
  role: '',
  metadataJson: '',
});

const distributionHasData = (summary: TipDistributionSummary | null): boolean =>
  Boolean(summary && summary.runs.some((run) => run.distributions.length > 0));

const formatPeriodLabel = (period: PayrollPeriodSummary): string => {
  try {
    const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    const start = formatter.format(new Date(period.startDate));
    const end = formatter.format(new Date(period.endDate));
    return `${start} \u2013 ${end}`;
  } catch {
    return `${period.startDate} \u2013 ${period.endDate}`;
  }
};


export const TipPoolingWorkspace = () => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const poolsQuery = useQuery({
    queryKey: ['tip-pools'],
    queryFn: listTipPools,
  });
  const pools = useMemo(() => poolsQuery.data ?? [], [poolsQuery.data]);

  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);

  useEffect(() => {
    if (pools.length === 0) {
      setSelectedPoolId(null);
      return;
    }
    if (!selectedPoolId || !pools.some((pool) => pool.id === selectedPoolId)) {
      setSelectedPoolId(pools[0].id);
    }
  }, [pools, selectedPoolId]);

  const assignmentsQuery = useQuery({
    queryKey: ['tip-pools', selectedPoolId, 'assignments'],
    queryFn: () => (selectedPoolId ? listTipPoolAssignments(selectedPoolId) : Promise.resolve([])),
    enabled: Boolean(selectedPoolId),
  });

  const employeesQuery = useQuery({
    queryKey: ['payroll', 'employees'],
    queryFn: async () => {
      const response = await listPayrollEmployees();
      return response.employees ?? [];
    },
  });

  const payrollPeriodsQuery = useQuery({
    queryKey: ['timekeeping', 'periods', 'tip-distribution'],
    queryFn: () => fetchPayrollPeriods({ status: ['draft', 'review', 'locked'] }),
  });
  const payrollPeriods = useMemo(() => payrollPeriodsQuery.data ?? [], [payrollPeriodsQuery.data]);

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);

  useEffect(() => {
    if (payrollPeriods.length === 0) {
      setSelectedPeriodId(null);
      return;
    }
    if (!selectedPeriodId || !payrollPeriods.some((period) => period.id === selectedPeriodId)) {
      setSelectedPeriodId(payrollPeriods[0].id);
    }
  }, [payrollPeriods, selectedPeriodId]);

  const distributionSummaryQuery = useQuery({
    queryKey: ['tip-distribution', selectedPeriodId],
    queryFn: () => getTipDistributionSummary(selectedPeriodId!),
    enabled: Boolean(selectedPeriodId),
  });
  const distributionSummary = distributionSummaryQuery.data ?? null;

  const employeesById = useMemo(() => {
    const map = new Map<string, PayrollEmployeeRecord>();
    for (const employee of employeesQuery.data ?? []) {
      map.set(employee.id, employee);
    }
    return map;
  }, [employeesQuery.data]);

  const selectedPool = selectedPoolId ? pools.find((pool) => pool.id === selectedPoolId) ?? null : null;

  const createPoolMutation = useMutation({
    mutationFn: (payload: CreateTipPoolPayload) => createTipPool(payload),
    onSuccess: (pool) => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.poolCreated', { defaultValue: 'Tip pool created.' }),
      });
      setSelectedPoolId(pool.id);
      queryClient.invalidateQueries({ queryKey: ['tip-pools'] });
      setPoolDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const updatePoolMutation = useMutation({
    mutationFn: ({ poolId, payload }: { poolId: string; payload: UpdateTipPoolPayload }) =>
      updateTipPool(poolId, payload),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.poolUpdated', { defaultValue: 'Tip pool updated.' }),
      });
      queryClient.invalidateQueries({ queryKey: ['tip-pools'] });
      setPoolDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const deletePoolMutation = useMutation({
    mutationFn: (poolId: string) => deleteTipPool(poolId),
    onSuccess: (_, poolId) => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.poolDeleted', { defaultValue: 'Tip pool deleted.' }),
      });
      if (selectedPoolId === poolId) {
        setSelectedPoolId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['tip-pools'] });
      queryClient.invalidateQueries({ queryKey: ['tip-distribution'] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const upsertAssignmentMutation = useMutation({
    mutationFn: ({
      poolId,
      assignmentId,
      payload,
    }: {
      poolId: string;
      assignmentId?: string | null;
      payload: UpsertTipPoolAssignmentPayload;
    }) => (assignmentId ? updateTipPoolAssignment(poolId, assignmentId, payload) : createTipPoolAssignment(poolId, payload)),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.assignmentSaved', { defaultValue: 'Assignment saved.' }),
      });
      queryClient.invalidateQueries({ queryKey: ['tip-pools', selectedPoolId, 'assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tip-distribution'] });
      setAssignmentDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: ({ poolId, assignmentId }: { poolId: string; assignmentId: string }) =>
      deleteTipPoolAssignment(poolId, assignmentId),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.assignmentDeleted', { defaultValue: 'Assignment removed.' }),
      });
      queryClient.invalidateQueries({ queryKey: ['tip-pools', selectedPoolId, 'assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tip-distribution'] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const rerunMutation = useMutation({
    mutationFn: (periodId: string) => rerunTipDistribution(periodId),
    onSuccess: (_, periodId) => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:tipPooling.distributionRerun', {
          defaultValue: 'Tip distribution recalculated.',
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['tip-distribution', periodId] });
      queryClient.invalidateQueries({ queryKey: ['tip-distribution'] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const [isPoolDialogOpen, setPoolDialogOpen] = useState(false);
  const [poolDialogMode, setPoolDialogMode] = useState<'create' | 'edit'>('create');
  const [poolFormValues, setPoolFormValues] = useState<TipPoolFormValues>(() => preparePoolFormValues());

  const openCreatePoolDialog = () => {
    setPoolDialogMode('create');
    setPoolFormValues(preparePoolFormValues());
    setPoolDialogOpen(true);
  };

  const openEditPoolDialog = (pool: TipPool) => {
    setPoolDialogMode('edit');
    setPoolFormValues(preparePoolFormValues(pool));
    setPoolDialogOpen(true);
  };

  const handlePoolDialogSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parseResult = TipPoolFormSchema.safeParse(poolFormValues);
    if (!parseResult.success) {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          parseResult.error.errors[0]?.message ??
          t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
      return;
    }

    const values = parseResult.data;
    const payload: CreateTipPoolPayload = {
      name: values.name.trim(),
      method: values.method,
      locationId: values.locationId?.trim() ? values.locationId.trim() : null,
      config: buildConfigForMethod(values.method, values.configJson ?? ''),
      isActive: values.isActive,
    };

    if (poolDialogMode === 'create') {
      createPoolMutation.mutate(payload);
    } else if (selectedPoolId) {
      updatePoolMutation.mutate({ poolId: selectedPoolId, payload });
    }
  };

  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentEditTarget, setAssignmentEditTarget] = useState<TipPoolAssignment | null>(null);
  const [assignmentFormValues, setAssignmentFormValues] = useState<AssignmentFormValues>(() =>
    defaultAssignmentFormValues(),
  );

  const openCreateAssignmentDialog = () => {
    setAssignmentEditTarget(null);
    setAssignmentFormValues(defaultAssignmentFormValues());
    setAssignmentDialogOpen(true);
  };

  const openEditAssignmentDialog = (assignment: TipPoolAssignment) => {
    setAssignmentEditTarget(assignment);
    setAssignmentFormValues({
      employeeId: assignment.employeeId,
      weight: assignment.weight.toString(),
      role: assignment.role ?? '',
      metadataJson: safeJsonStringify(assignment.metadata ?? {}),
    });
    setAssignmentDialogOpen(true);
  };

  const handleAssignmentDialogSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parseResult = AssignmentFormSchema.safeParse(assignmentFormValues);
    if (!parseResult.success) {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          parseResult.error.errors[0]?.message ??
          t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
      return;
    }
    if (!selectedPoolId) {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: t('payrollBridge:tipPooling.selectPoolFirst', {
          defaultValue: 'Select a pool before managing assignments.',
        }),
      });
      return;
    }

    const values = parseResult.data;
    const weightValue = values.weight && values.weight.trim().length > 0 ? Number(values.weight) : 1;
    if (Number.isNaN(weightValue) || weightValue < 0) {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: t('payrollBridge:tipPooling.invalidWeight', {
          defaultValue: 'Enter a valid weight of zero or greater.',
        }),
      });
      return;
    }

    let metadata: Record<string, unknown> = {};
    if (values.metadataJson && values.metadataJson.trim().length > 0) {
      try {
        const parsed = JSON.parse(values.metadataJson);
        if (parsed && typeof parsed === 'object') {
          metadata = parsed as Record<string, unknown>;
        }
      } catch {
        toast({
          variant: 'destructive',
          title: t('common:status.error', { defaultValue: 'Something went wrong' }),
          description: t('payrollBridge:tipPooling.jsonParseError', {
            defaultValue: 'Metadata must be valid JSON.',
          }),
        });
        return;
      }
    }

    const payload: UpsertTipPoolAssignmentPayload = {
      employeeId: values.employeeId,
      weight: weightValue,
      role: values.role ? values.role : null,
      metadata,
    };

    upsertAssignmentMutation.mutate({
      poolId: selectedPoolId,
      assignmentId: assignmentEditTarget?.id ?? null,
      payload,
    });
  };

  const handleDeletePool = (poolId: string) => {
    const confirmed = window.confirm(
      t('payrollBridge:tipPooling.deletePoolConfirm', {
        defaultValue: 'Remove this tip pool? Existing distributions stay in the audit log.',
      }),
    );
    if (!confirmed) {
      return;
    }
    deletePoolMutation.mutate(poolId);
  };

  const assignmentRows = assignmentsQuery.data ?? [];
  const poolTotals = summarizePoolTotals(distributionSummary);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t('payrollBridge:tipPooling.title', { defaultValue: 'Tip pooling workspace' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('payrollBridge:tipPooling.subtitle', {
              defaultValue: 'Configure pools, manage assignments, and simulate distributions before payroll runs.',
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!selectedPeriodId || rerunMutation.isLoading}
            onClick={() => selectedPeriodId && rerunMutation.mutate(selectedPeriodId)}
          >
            <RefreshCcw className={cn('mr-2 h-4 w-4', rerunMutation.isLoading && 'animate-spin')} />
            {t('payrollBridge:tipPooling.rerunDistribution', { defaultValue: 'Recompute distribution' })}
          </Button>
          <Button onClick={openCreatePoolDialog}>
            <Plus className="mr-2 h-4 w-4" />
            {t('payrollBridge:tipPooling.newPool', { defaultValue: 'New pool' })}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1 border border-border shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <CardHeader className="space-y-2">
            <CardTitle className="text-base font-semibold text-foreground">
              {t('payrollBridge:tipPooling.poolListTitle', { defaultValue: 'Tip pools' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:tipPooling.poolListDescription', {
                defaultValue: 'Shared pools appear first; location-specific pools follow.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {poolsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : poolsQuery.isError ? (
              <p className="text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.cardError', {
                  defaultValue: 'Unable to load tip pools. Try again shortly.',
                })}
              </p>
            ) : pools.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/50 p-6 text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.cardEmpty', {
                  defaultValue: 'No tip pools yet. Create your first pool to automate tip outs.',
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {pools.map((pool) => (
                  <button
                    key={pool.id}
                    type="button"
                    onClick={() => setSelectedPoolId(pool.id)}
                    className={cn(
                      'flex w-full items-start justify-between rounded-lg border p-4 text-left transition hover:border-primary/60 dark:border-slate-800/70',
                      selectedPoolId === pool.id ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-border bg-card dark:bg-slate-900/40',
                    )}
                  >
                    <div>
                      <p className="font-semibold text-foreground">{pool.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t(`payrollBridge:tipPooling.method.${pool.method}`, {
                          defaultValue: pool.method.replace('_', ' '),
                        })}
                        {pool.locationId ? ` · ${pool.locationId}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={pool.isActive ? 'secondary' : 'outline'}>
                        {pool.isActive
                          ? t('payrollBridge:tipPooling.statusActive', { defaultValue: 'Active' })
                          : t('payrollBridge:tipPooling.statusInactive', { defaultValue: 'Inactive' })}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditPoolDialog(pool);
                        }}
                        aria-label={t('payrollBridge:tipPooling.editPool', { defaultValue: 'Edit pool' })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeletePool(pool.id);
                        }}
                        aria-label={t('payrollBridge:tipPooling.deletePool', { defaultValue: 'Delete pool' })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-border shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="text-base font-semibold text-foreground">
                {t('payrollBridge:tipPooling.assignmentsTitle', { defaultValue: 'Assignments' })}
              </CardTitle>
              <CardDescription>
                {selectedPool
                  ? t('payrollBridge:tipPooling.assignmentsDescription', {
                      defaultValue: 'Assign team members and adjust weights for {{poolName}}.',
                      poolName: selectedPool.name,
                    })
                  : t('payrollBridge:tipPooling.assignmentsPlaceholder', {
                      defaultValue: 'Select a tip pool to manage assignments.',
                    })}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="px-2 py-0.5 text-xs font-semibold">
                {t(`payrollBridge:tipPooling.method.${selectedPool?.method ?? 'hours'}`, {
                  defaultValue: selectedPool?.method ?? 'hours',
                })}
              </Badge>
              <Button variant="outline" size="sm" onClick={openCreateAssignmentDialog} disabled={!selectedPoolId}>
                <Users className="mr-2 h-4 w-4" />
                {t('payrollBridge:tipPooling.addAssignment', { defaultValue: 'Add assignment' })}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedPoolId ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.assignmentsPlaceholder', {
                  defaultValue: 'Select a tip pool to manage assignments.',
                })}
              </div>
            ) : assignmentsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : assignmentRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
                {t('payrollBridge:tipPooling.noAssignments', {
                  defaultValue: 'No assignments yet. Add team members to this pool.',
                })}
              </div>
            ) : (
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('payrollBridge:tipPooling.employee', { defaultValue: 'Employee' })}</TableHead>
                      <TableHead>{t('payrollBridge:tipPooling.weight', { defaultValue: 'Weight' })}</TableHead>
                      <TableHead>{t('payrollBridge:tipPooling.role', { defaultValue: 'Role' })}</TableHead>
                      <TableHead className="w-32 text-right">
                        {t('payrollBridge:tipPooling.actions', { defaultValue: 'Actions' })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignmentRows.map((assignment) => {
                      const employee = employeesById.get(assignment.employeeId);
                      return (
                        <TableRow key={assignment.id}>
                          <TableCell>
                            <div className="font-medium text-foreground">
                              {employee?.legalName ?? assignment.employeeId}
                            </div>
                            {employee?.position ? (
                              <div className="text-xs text-muted-foreground">{employee.position}</div>
                            ) : null}
                          </TableCell>
                          <TableCell>{assignment.weight.toFixed(2)}</TableCell>
                          <TableCell>{assignment.role ?? '\u2014'}</TableCell>
                          <TableCell className="space-x-2 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditAssignmentDialog(assignment)}
                              aria-label={t('common:actions.edit', { defaultValue: 'Edit' })}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                deleteAssignmentMutation.mutate({
                                  poolId: assignment.poolId,
                                  assignmentId: assignment.id,
                                })
                              }
                              aria-label={t('common:actions.delete', { defaultValue: 'Delete' })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-base font-semibold text-foreground">
              {t('payrollBridge:tipPooling.distributionTitle', { defaultValue: 'Distribution history' })}
            </CardTitle>
            <CardDescription>
              {t('payrollBridge:tipPooling.distributionSubtitle', {
                defaultValue: 'Review completed runs from the selected payroll period.',
              })}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="tip-pooling-period" className="text-sm text-muted-foreground">
              {t('payrollBridge:tipPooling.selectPeriod', { defaultValue: 'Payroll period' })}
            </Label>
            <Select value={selectedPeriodId ?? ''} onValueChange={setSelectedPeriodId}>
              <SelectTrigger id="tip-pooling-period" className="w-48">
                <SelectValue
                  placeholder={t('payrollBridge:tipPooling.selectPeriodPlaceholder', {
                    defaultValue: 'Choose period',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {payrollPeriods.map((period) => (
                  <SelectItem key={period.id} value={period.id}>
                    {formatPeriodLabel(period)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {distributionSummaryQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : !selectedPeriodId ? (
            <p className="text-sm text-muted-foreground">
              {t('payrollBridge:tipPooling.selectPeriodPlaceholder', { defaultValue: 'Choose period' })}
            </p>
          ) : !distributionHasData(distributionSummary) ? (
            <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
              {t('payrollBridge:tipPooling.noDistributions', {
                defaultValue: 'No tip distributions recorded for the selected payroll period.',
              })}
            </div>
          ) : (
            <>
              {distributionSummary?.totals ? (
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="px-2 py-0.5 font-medium">
                    {t('payrollBridge:tipPooling.totalsPools', {
                      defaultValue: '{{count}} pools',
                      count: distributionSummary.totals.poolCount,
                    })}
                  </Badge>
                  <Badge variant="outline" className="px-2 py-0.5 font-medium">
                    {t('payrollBridge:tipPooling.totalsRuns', {
                      defaultValue: '{{count}} runs',
                      count: distributionSummary.totals.runCount,
                    })}
                  </Badge>
                  <Badge variant="outline" className="px-2 py-0.5 font-medium">
                    {t('payrollBridge:tipPooling.totalsDistributed', {
                      defaultValue: '{{amount}} distributed',
                      amount: formatCurrency(distributionSummary.totals.distributedTipCents),
                    })}
                  </Badge>
                  {distributionSummary.totals.remainderCents > 0 ? (
                    <Badge variant="destructive" className="px-2 py-0.5 font-medium">
                      {t('payrollBridge:tipPooling.totalsRemainder', {
                        defaultValue: '{{amount}} remainder',
                        amount: formatCurrency(distributionSummary.totals.remainderCents),
                      })}
                    </Badge>
                  ) : null}
                </div>
              ) : null}

              {poolTotals.length > 0 ? (
                <div className="rounded-lg border border-border bg-muted/20 p-4 text-xs text-muted-foreground dark:border-slate-800/70 dark:bg-slate-900/40">
                  <p className="font-semibold text-foreground">
                    {t('payrollBridge:tipPooling.poolTotalsTitle', { defaultValue: 'Pooled totals' })}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {poolTotals.slice(0, 5).map((item) => (
                      <li key={item.poolName} className="flex items-center justify-between">
                        <span>{item.poolName}</span>
                        <span>{formatCurrency(item.amountCents)}</span>
                      </li>
                    ))}
                    {poolTotals.length > 5 ? (
                      <li className="text-right">
                        {t('payrollBridge:tipPooling.morePools', {
                          defaultValue: '+{{count}} more',
                          count: poolTotals.length - 5,
                        })}
                      </li>
                    ) : null}
                  </ul>
                </div>
              ) : null}

              <div className="space-y-4">
                {distributionSummary?.runs.map((run) => (
                  <Card key={run.runId} className="border border-border/70 bg-card shadow-sm dark:border-slate-800/60 dark:bg-slate-900/50">
                    <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <CardTitle className="text-base font-semibold text-foreground">{run.poolName}</CardTitle>
                        <CardDescription className="text-xs text-muted-foreground">
                          {t('payrollBridge:tipPooling.runSummary', {
                            defaultValue: '{{tipType}} · {{amount}} distributed',
                            tipType: run.tipType.replace('_', ' '),
                            amount: formatCurrency(run.distributedTipCents),
                          })}
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                          {t(`payrollBridge:tipPooling.method.${run.method}`, {
                            defaultValue: run.method.replace('_', ' '),
                          })}
                        </Badge>
                        {run.remainderCents > 0 ? (
                          <Badge variant="destructive">
                            {t('payrollBridge:tipPooling.remainderBadge', {
                              defaultValue: '{{amount}} remainder',
                              amount: formatCurrency(run.remainderCents),
                            })}
                          </Badge>
                        ) : null}
                        <Badge variant="secondary">
                          {t('payrollBridge:tipPooling.assignmentsCount', {
                            defaultValue: 'Assignments: {{count}}',
                            count: run.assignmentCount,
                          })}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                        <span>
                          {t('payrollBridge:tipPooling.grossTips', { defaultValue: 'Gross tips:' })}{' '}
                          {formatCurrency(run.grossTipCents)}
                        </span>
                        <span>
                          {t('payrollBridge:tipPooling.ledgerCount', {
                            defaultValue: 'Ledger rows: {{count}}',
                            count: run.ledgerCount ?? 0,
                          })}
                        </span>
                        <span>
                          {t('payrollBridge:tipPooling.completedAt', {
                            defaultValue: 'Completed at: {{value}}',
                            value: run.completedAt ? new Date(run.completedAt).toLocaleString() : t('payrollBridge:tipPooling.pending', { defaultValue: 'Pending' }),
                          })}
                        </span>
                      </div>
                      <Separator />
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('payrollBridge:tipPooling.employee', { defaultValue: 'Employee' })}</TableHead>
                            <TableHead>{t('payrollBridge:tipPooling.tipType', { defaultValue: 'Tip type' })}</TableHead>
                            <TableHead className="text-right">
                              {t('payrollBridge:tipPooling.amount', { defaultValue: 'Amount' })}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {run.distributions.map((distribution) => {
                            const employee = employeesById.get(distribution.employeeId);
                            return (
                              <TableRow key={distribution.id}>
                                <TableCell>{employee?.legalName ?? distribution.employeeId}</TableCell>
                                <TableCell>{distribution.tipType.replace('_', ' ')}</TableCell>
                                <TableCell className="text-right">{formatCurrency(distribution.amountCents)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isPoolDialogOpen} onOpenChange={setPoolDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {poolDialogMode === 'create'
                ? t('payrollBridge:tipPooling.poolDialogTitleCreate', { defaultValue: 'Create tip pool' })
                : t('payrollBridge:tipPooling.poolDialogTitleEdit', { defaultValue: 'Edit tip pool' })}
            </DialogTitle>
            <DialogDescription>
              {t('payrollBridge:tipPooling.poolDialogDescription', {
                defaultValue: 'Name the pool, choose a method, and adjust configuration as needed.',
              })}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handlePoolDialogSubmit}>
            <div className="space-y-2">
              <Label htmlFor="pool-name">
                {t('payrollBridge:tipPooling.poolNameLabel', { defaultValue: 'Pool name' })}
              </Label>
              <Input
                id="pool-name"
                value={poolFormValues.name}
                onChange={(event) =>
                  setPoolFormValues((current) => ({ ...current, name: event.target.value }))
                }
                placeholder={t('payrollBridge:tipPooling.poolNamePlaceholder', {
                  defaultValue: 'Front of house',
                })}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pool-method">
                  {t('payrollBridge:tipPooling.methodLabel', { defaultValue: 'Method' })}
                </Label>
                <Select
                  value={poolFormValues.method}
                  onValueChange={(value) =>
                    setPoolFormValues((current) => ({
                      ...current,
                      method: value as TipPoolMethod,
                      configJson: safeJsonStringify(DEFAULT_CONFIGS[value as TipPoolMethod]),
                    }))
                  }
                >
                  <SelectTrigger id="pool-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hours">
                      {t('payrollBridge:tipPooling.method.hours', { defaultValue: 'Hours based' })}
                    </SelectItem>
                    <SelectItem value="role_weight">
                      {t('payrollBridge:tipPooling.method.role_weight', { defaultValue: 'Role weight' })}
                    </SelectItem>
                    <SelectItem value="percentage">
                      {t('payrollBridge:tipPooling.method.percentage', { defaultValue: 'Percentage' })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pool-status">
                  {t('payrollBridge:tipPooling.statusLabel', { defaultValue: 'Status' })}
                </Label>
                <Select
                  value={poolFormValues.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) =>
                    setPoolFormValues((current) => ({ ...current, isActive: value === 'active' }))
                  }
                >
                  <SelectTrigger id="pool-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      {t('payrollBridge:tipPooling.statusActive', { defaultValue: 'Active' })}
                    </SelectItem>
                    <SelectItem value="inactive">
                      {t('payrollBridge:tipPooling.statusInactive', { defaultValue: 'Inactive' })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pool-location">
                {t('payrollBridge:tipPooling.locationLabel', { defaultValue: 'Location scope (optional)' })}
              </Label>
              <Input
                id="pool-location"
                value={poolFormValues.locationId}
                onChange={(event) =>
                  setPoolFormValues((current) => ({ ...current, locationId: event.target.value }))
                }
                placeholder={t('payrollBridge:tipPooling.locationPlaceholder', {
                  defaultValue: 'Leave blank for all locations',
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pool-config">
                {t('payrollBridge:tipPooling.configLabel', { defaultValue: 'Method configuration' })}
              </Label>
              <Textarea
                id="pool-config"
                value={poolFormValues.configJson}
                onChange={(event) =>
                  setPoolFormValues((current) => ({ ...current, configJson: event.target.value }))
                }
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                {t('payrollBridge:tipPooling.configHelp', {
                  defaultValue: 'Provide advanced configuration as JSON. Defaults are pre-filled for each method.',
                })}
              </p>
            </div>

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setPoolDialogOpen(false)}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={createPoolMutation.isLoading || updatePoolMutation.isLoading}>
                {t('common:actions.save', { defaultValue: 'Save' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {assignmentEditTarget
                ? t('payrollBridge:tipPooling.assignmentDialogTitleEdit', { defaultValue: 'Edit assignment' })
                : t('payrollBridge:tipPooling.assignmentDialogTitleCreate', { defaultValue: 'Add assignment' })}
            </DialogTitle>
            <DialogDescription>
              {t('payrollBridge:tipPooling.assignmentDialogDescription', {
                defaultValue: 'Select a team member, assign a weight, and optionally include metadata.',
              })}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAssignmentDialogSubmit}>
            <div className="space-y-2">
              <Label htmlFor="assignment-employee">
                {t('payrollBridge:tipPooling.assignmentEmployeeLabel', { defaultValue: 'Employee' })}
              </Label>
              <Select
                value={assignmentFormValues.employeeId}
                onValueChange={(value) =>
                  setAssignmentFormValues((current) => ({ ...current, employeeId: value }))
                }
              >
                <SelectTrigger id="assignment-employee">
                  <SelectValue
                    placeholder={t('payrollBridge:tipPooling.assignmentEmployeePlaceholder', {
                      defaultValue: 'Choose team member',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(employeesQuery.data ?? []).map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.legalName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="assignment-weight">
                  {t('payrollBridge:tipPooling.assignmentWeightLabel', { defaultValue: 'Weight' })}
                </Label>
                <Input
                  id="assignment-weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={assignmentFormValues.weight}
                  onChange={(event) =>
                    setAssignmentFormValues((current) => ({ ...current, weight: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="assignment-role">
                  {t('payrollBridge:tipPooling.assignmentRoleLabel', { defaultValue: 'Role override' })}
                </Label>
                <Input
                  id="assignment-role"
                  value={assignmentFormValues.role}
                  onChange={(event) =>
                    setAssignmentFormValues((current) => ({ ...current, role: event.target.value }))
                  }
                  placeholder={t('payrollBridge:tipPooling.assignmentRolePlaceholder', {
                    defaultValue: 'Optional role alias',
                  })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignment-metadata">
                {t('payrollBridge:tipPooling.assignmentMetadataLabel', { defaultValue: 'Metadata (JSON)' })}
              </Label>
              <Textarea
                id="assignment-metadata"
                rows={4}
                value={assignmentFormValues.metadataJson}
                onChange={(event) =>
                  setAssignmentFormValues((current) => ({ ...current, metadataJson: event.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                {t('payrollBridge:tipPooling.assignmentMetadataHelp', {
                  defaultValue: 'Optional JSON for calculators (for example {"overtimeMultiplier":1.5}).',
                })}
              </p>
            </div>

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setAssignmentDialogOpen(false)}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={upsertAssignmentMutation.isLoading}>
                {t('common:actions.save', { defaultValue: 'Save' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TipPoolingWorkspace;
